import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRole } from '../../contexts/RoleContext';
import {
  addProvider,
  archiveProvider,
  fetchProviders,
  updateProvider,
} from '../../services/providerService';
import { linkDoctorVisitProvider } from '../../services/doctorVisitService';
import {
  PROVIDER_ROLE_OPTIONS,
  getProviderRoleLabel,
} from '../../constants/providers';
import ProviderCard from './ProviderCard';
import ProviderDialog from './ProviderDialog';
import CareProfileBackButton from '../../components/UI/CareProfileBackButton';
import {
  buildProviderReviewReturnState,
  getProviderReviewNavigationContext,
} from '../../utils/providerReviewNavigation';

const emptyForm = (childId = '') => ({
  childId,
  name: '',
  role: 'doctor',
  specialty: '',
  organizationName: '',
  phone: '',
  email: '',
  address: '',
  notes: '',
  isActive: true,
  source: {
    kind: 'manual',
    sourceId: null,
    sourceField: null,
    sourceLabel: null,
    sourceText: null,
    confidence: null,
  },
});

const normalizeText = (value = '') => String(value || '').trim().toLowerCase();
const EMPTY_ROUTE_STATE = {};

const getProviderMatchKey = (provider = {}) => [
  provider.name,
  provider.specialty,
  provider.organizationName,
].map(normalizeText).join('|');

const ProvidersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { childrenWithAccess = [], loading: roleLoading } = useRole();
  const routeState = location.state || EMPTY_ROUTE_STATE;
  const reviewContext = useMemo(
    () => getProviderReviewNavigationContext({ ...routeState, ...(routeState.providerReview || {}) }),
    [routeState]
  );
  const hasReviewOriginContext = reviewContext.hasOriginContext;
  const routeChildId = String(reviewContext.childId || routeState.childId || '').trim();
  const hasRouteChildContext = Boolean(routeChildId && childrenWithAccess.some((child) => child.id === routeChildId));
  const shouldHideChildSelector = Boolean(routeChildId) && (roleLoading || hasRouteChildContext || hasReviewOriginContext);
  const [selectedChildId, setSelectedChildId] = useState(() => routeChildId);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [selectedChildIds, setSelectedChildIds] = useState([]);

  useEffect(() => {
    if (!selectedChildId && childrenWithAccess.length > 0) {
      setSelectedChildId(routeChildId || childrenWithAccess[0].id);
      return;
    }

    if (selectedChildId && !childrenWithAccess.some((child) => child.id === selectedChildId)) {
      setSelectedChildId(routeChildId && childrenWithAccess.some((child) => child.id === routeChildId) ? routeChildId : (childrenWithAccess[0]?.id || ''));
    }
  }, [childrenWithAccess, routeChildId, selectedChildId]);

  useEffect(() => {
    const loadProviders = async () => {
      if (!selectedChildId) {
        setProviders([]);
        return;
      }

      setLoading(true);
      try {
        const nextProviders = await fetchProviders(selectedChildId);
        setProviders(nextProviders);
      } catch (error) {
        setProviders([]);
      } finally {
        setLoading(false);
      }
    };

    loadProviders();
  }, [selectedChildId]);

  const selectedChild = useMemo(
    () => childrenWithAccess.find((child) => child.id === selectedChildId) || null,
    [childrenWithAccess, selectedChildId]
  );

  const sortedProviders = useMemo(() => (
    [...providers].sort((left, right) => {
      if (left.isActive !== right.isActive) {
        return left.isActive ? -1 : 1;
      }

      return String(left.name || '').localeCompare(String(right.name || ''));
    })
  ), [providers]);

  const reviewProviderSnapshot = reviewContext.providerReview || null;
  const reviewVisitId = reviewContext.visitId || reviewContext.originSourceId || reviewContext.originEntryId || '';
  const reviewReturnState = useMemo(() => buildProviderReviewReturnState(reviewContext), [reviewContext]);
  const reviewReturnTo = reviewContext.returnTo || '/care-profile';

  const handleReturnToOrigin = () => {
    if (!hasReviewOriginContext) {
      navigate('/care-profile', {
        state: {
          childId: selectedChildId || null,
        },
      });
      return;
    }

    navigate(reviewReturnTo, {
      state: reviewReturnState,
    });
  };

  const handleOpenCreate = () => {
    const defaultChildId = selectedChildId || childrenWithAccess[0]?.id || routeChildId || '';
    setEditingProvider(null);
    setForm({
      ...emptyForm(defaultChildId),
      name: reviewProviderSnapshot?.providerName || '',
      specialty: reviewProviderSnapshot?.providerSpecialty || '',
      organizationName: reviewProviderSnapshot?.organizationName || '',
      source: {
        ...emptyForm().source,
        kind: 'manual',
      },
    });
    setSelectedChildIds(defaultChildId ? [defaultChildId] : []);
    setDialogOpen(true);
  };

  const handleOpenEdit = (provider) => {
    setEditingProvider(provider);
    setForm({
      ...emptyForm(provider.childId),
      ...provider,
      source: provider.source || emptyForm().source,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProvider(null);
    setForm(emptyForm(selectedChildId));
    setSelectedChildIds([]);
  };

  const handleChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const refreshProviders = async () => {
    if (!selectedChildId) return;
    const nextProviders = await fetchProviders(selectedChildId);
    setProviders(nextProviders);
  };

  const findExactProviderMatch = (candidate = {}) => providers.find((provider) => (
    provider.childId === selectedChildId
    && getProviderMatchKey(provider) === getProviderMatchKey(candidate)
  )) || null;

  const syncDoctorVisitLink = async (providerId) => {
    if (!hasReviewOriginContext || !reviewVisitId || !providerId) {
      return;
    }

    await linkDoctorVisitProvider(reviewVisitId, {
      providerId,
    });
  };

  const handleSave = async () => {
    const childIds = Array.from(new Set((selectedChildIds.length ? selectedChildIds : [selectedChildId]).filter(Boolean)));
    const primaryChildId = selectedChildId || childIds[0] || routeChildId || '';
    const payload = {
      ...form,
      childId: primaryChildId,
      source: {
        ...form.source,
        kind: form.source?.kind || 'manual',
      },
    };

    if (editingProvider) {
      await updateProvider(editingProvider.id, payload);
    } else {
      const exactMatch = hasReviewOriginContext ? findExactProviderMatch(payload) : null;

      if (exactMatch) {
        const extraChildIds = childIds.filter((childId) => childId !== primaryChildId);
        await Promise.all(extraChildIds.map((childId) => addProvider({
          ...payload,
          childId,
        })));
        await syncDoctorVisitLink(exactMatch.id);
      } else {
        const createdProviderIds = await Promise.all(childIds.map((childId) => addProvider({
          ...payload,
          childId,
        })));
        const primaryIndex = Math.max(childIds.indexOf(primaryChildId), 0);
        await syncDoctorVisitLink(createdProviderIds[primaryIndex] || createdProviderIds[0]);
      }
    }

    await refreshProviders();
    handleCloseDialog();
    handleReturnToOrigin();
  };

  const handleLinkExistingProvider = async (provider) => {
    await syncDoctorVisitLink(provider.id);
    handleReturnToOrigin();
  };

  const handleArchive = async (provider) => {
    await archiveProvider(provider.id);
    await refreshProviders();
  };

  const handleSkipReview = () => {
    handleReturnToOrigin();
  };

  const isEmpty = !roleLoading && !selectedChildId;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack spacing={2.5}>
        <Box>
          <CareProfileBackButton
            onClick={handleReturnToOrigin}
            ariaLabel={hasReviewOriginContext ? 'Back' : 'Back to Profile'}
            sx={{
              mb: 1,
            }}
          />
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Providers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Canonical provider records for the selected child.
          </Typography>
        </Box>

        {hasReviewOriginContext ? (
          <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  Review provider
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {reviewProviderSnapshot?.providerName || 'Unknown provider'}
                  {reviewProviderSnapshot?.providerSpecialty ? ` · ${reviewProviderSnapshot.providerSpecialty}` : ''}
                  {reviewProviderSnapshot?.organizationName ? ` · ${reviewProviderSnapshot.organizationName}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button variant="contained" onClick={handleOpenCreate} disabled={!selectedChildId}>
                  Add provider
                </Button>
                <Button variant="outlined" onClick={handleSkipReview}>
                  Skip
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : null}

        {shouldHideChildSelector ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              disabled={!selectedChildId}
            >
              Add provider
            </Button>
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl fullWidth sx={{ maxWidth: 340 }}>
              <InputLabel id="provider-child-label">Child</InputLabel>
              <Select
                labelId="provider-child-label"
                label="Child"
                value={selectedChildId}
                onChange={(event) => setSelectedChildId(event.target.value)}
              >
                {childrenWithAccess.map((child) => (
                  <MenuItem key={child.id} value={child.id}>
                    {child.name || 'Child'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreate}
              disabled={!selectedChildId}
            >
              Add provider
            </Button>
          </Stack>
        )}

        {isEmpty ? (
          <Alert severity="info">No child selected.</Alert>
        ) : null}

        {selectedChild ? (
          <Typography variant="body2" color="text.secondary">
            Showing providers for {selectedChild.name || 'this child'}.
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : sortedProviders.length === 0 && selectedChildId ? (
          <Alert severity="info">No providers yet.</Alert>
        ) : null}

        <Stack spacing={1.5}>
          {sortedProviders.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              roleLabel={getProviderRoleLabel(provider.role)}
              onEdit={() => handleOpenEdit(provider)}
              onArchive={() => handleArchive(provider)}
              onLink={hasReviewOriginContext ? () => handleLinkExistingProvider(provider) : null}
            />
          ))}
        </Stack>
      </Stack>

      <ProviderDialog
        open={dialogOpen}
        editingProvider={editingProvider}
        form={form}
        onChange={handleChange}
        onClose={handleCloseDialog}
        onSave={handleSave}
        roleOptions={PROVIDER_ROLE_OPTIONS}
        childOptions={childrenWithAccess}
        currentChildId={selectedChildId}
        selectedChildIds={selectedChildIds}
        onSelectedChildIdsChange={setSelectedChildIds}
        showChildSelection={!editingProvider}
      />
    </Box>
  );
};

export default ProvidersPage;
